import { lazy, type ComponentType } from 'react';
import { isChunkLoadError, reloadOnceForStaleChunk } from './chunk-reload';

/**
 * `React.lazy` for a named export, so each page is its own chunk. A chunk that fails to load
 * (stale tab after a deploy) reloads the tab once; if that already happened, the error reaches
 * the page error boundary, which offers Reload.
 */
export function lazyPage<M extends Record<string, unknown>, K extends keyof M>(
  load: () => Promise<M>,
  name: K,
) {
  return lazy(async () => {
    try {
      const m = await load();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy's own constraint
      return { default: m[name] as ComponentType<any> };
    } catch (e) {
      if (isChunkLoadError(e) && reloadOnceForStaleChunk()) {
        // The page is about to reload; never resolve so nothing flashes meanwhile.
        return new Promise<never>(() => undefined);
      }
      throw e;
    }
  }) as unknown as M[K];
}
